import { Code, Loader, Modal, Stack, Tabs, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { formatDate } from 'src/financial/financial-data/tables/FinancialItemGenericTable';
import { EntityDetectionPanel } from './EntityDetectionPanel';
import { MetadataRow } from './MetadataRow';
import { CODE_BLOCK_STYLE } from './styles';
import { DataUploadFileContent, ParsedFileContent } from './types';

function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MIME_TYPE_MAP: Record<string, string> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/msword': 'doc',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/pdf': 'pdf',
  'text/markdown': 'markdown',
  'text/plain': 'text',
  'image/png': 'png',
  'image/jpeg': 'jpeg',
  'video/mp4': 'mp4',
  'audio/wav': 'wav',
};

function formatFileType(mimeType: string | null | undefined): string {
  if (!mimeType) return '-';
  if (MIME_TYPE_MAP[mimeType]) return MIME_TYPE_MAP[mimeType];
  if (mimeType.startsWith('application/vnd.')) return mimeType.split('.').pop() ?? mimeType;
  return mimeType;
}

type DataUploadFileContentModalProps = {
  fileId: string | null;
  companyId: string;
  onClose: () => void;
};

function formatContent(content: string | string[] | null | undefined): string {
  if (!content) return '';
  if (Array.isArray(content)) return content.join('\n\n');
  return content;
}

function ParsedContentDisplay({
  parsed,
  label,
}: {
  parsed: ParsedFileContent | null | undefined;
  label: string;
}) {
  if (!parsed) {
    return <Text c="dimmed">No {label} content available</Text>;
  }

  const formattedContent = formatContent(parsed.content);
  const hasTables = parsed.tables && parsed.tables.length > 0;

  return (
    <Stack gap="md">
      {parsed.content !== null && (
        <Stack gap="xs">
          <Title order={5}>Content</Title>
          <Text size="sm" c="dimmed">
            {Array.isArray(parsed.content)
              ? `${parsed.content.length} sections`
              : `${formattedContent.length} characters`}
          </Text>
          <Code block style={CODE_BLOCK_STYLE}>
            {formattedContent || 'Empty content'}
          </Code>
        </Stack>
      )}

      {hasTables && (
        <Stack gap="xs">
          <Title order={5}>Tables</Title>
          <Text size="sm" c="dimmed">
            {parsed.tables!.length} table(s)
          </Text>
          <Code block style={CODE_BLOCK_STYLE}>
            {JSON.stringify(parsed.tables, null, 2)}
          </Code>
        </Stack>
      )}

      {parsed.content === null && !hasTables && (
        <Text c="dimmed">No {label} content available</Text>
      )}
    </Stack>
  );
}

export function DataUploadFileContentModal({
  fileId,
  companyId,
  onClose,
}: DataUploadFileContentModalProps) {
  const { data: content, isLoading } = useQuery<DataUploadFileContent>({
    queryKey: ['admin', 'companies', companyId, 'data-upload-files', fileId, 'content'],
    enabled: !!fileId,
  });

  return (
    <Modal
      opened={!!fileId}
      onClose={onClose}
      title={`Data upload file: ${content?.id ?? ''}`}
      size={1200}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <Tabs defaultValue="metadata">
          <Tabs.List style={{ width: 'fit-content' }}>
            <Tabs.Tab value="metadata">Metadata</Tabs.Tab>
            <Tabs.Tab value="file">File details</Tabs.Tab>
            <Tabs.Tab value="parsed">Parsed content</Tabs.Tab>
            <Tabs.Tab value="wrangled">Wrangled content</Tabs.Tab>
            <Tabs.Tab value="detection">Entity detection</Tabs.Tab>
            <Tabs.Tab value="process">Background process</Tabs.Tab>
            <Tabs.Tab value="model">Data upload model</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="metadata" pt="md">
            <Stack gap="xs">
              <MetadataRow label="id" value={content?.id} />
              <MetadataRow label="fileId" value={content?.fileId} />
              <MetadataRow label="clientId" value={content?.clientId} />
              <MetadataRow label="isFolder" value={content?.isFolder} />
              <MetadataRow label="isResolved" value={content?.isResolved} />
              <MetadataRow label="parentId" value={content?.parentId} />
              <MetadataRow label="createdAt" value={content?.createdAt} />
              <MetadataRow label="documentDate" value={content?.documentDate} />
              <MetadataRow label="backgroundProcessId" value={content?.backgroundProcessId} />
              <MetadataRow label="dataUploadModelId" value={content?.dataUploadModelId} />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="file" pt="md">
            {content?.file ? (
              <Stack gap="xs">
                <MetadataRow label="id" value={content.file.id} />
                <MetadataRow label="fileName" value={content.file.fileName} />
                <MetadataRow label="fileType" value={formatFileType(content.file.fileType)} />
                <MetadataRow label="fileSize" value={formatFileSize(content.file.fileSize)} />
                <MetadataRow label="location" value={content.file.location} />
                <MetadataRow label="userId" value={content.file.userId} />
                <MetadataRow label="isFolder" value={content.file.isFolder} />
                <MetadataRow label="isResolved" value={content.file.isResolved} />
                <MetadataRow label="isFiled" value={content.file.isFiled} />
                <MetadataRow label="isTemporary" value={content.file.isTemporary} />
                <MetadataRow label="parentId" value={content.file.parentId} />
                <MetadataRow label="templateId" value={content.file.templateId} />
                <MetadataRow
                  label="createdAt"
                  value={content.file.createdAt ? formatDate(content.file.createdAt) : '-'}
                />
              </Stack>
            ) : (
              <Text c="dimmed">No file information available</Text>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="parsed" pt="md">
            <ParsedContentDisplay parsed={content?.parsedContent} label="parsed" />
          </Tabs.Panel>

          <Tabs.Panel value="wrangled" pt="md">
            <ParsedContentDisplay parsed={content?.wrangledContent} label="wrangled" />
          </Tabs.Panel>

          <Tabs.Panel value="detection" pt="md">
            <EntityDetectionPanel entities={content?.cachedEntityDetection ?? []} />
          </Tabs.Panel>

          <Tabs.Panel value="process" pt="md">
            {content?.backgroundProcess ? (
              <Stack gap="xs">
                <MetadataRow label="id" value={content.backgroundProcess.id} />
                <MetadataRow label="processName" value={content.backgroundProcess.processName} />
                <MetadataRow label="status" value={content.backgroundProcess.status} />
                <MetadataRow
                  label="statusMessage"
                  value={content.backgroundProcess.statusMessage}
                />
                {content.backgroundProcess.extraDetails && (
                  <>
                    <Text size="sm" c="dimmed" mt="sm">
                      Extra details:
                    </Text>
                    <Code block style={{ maxHeight: '50vh', maxWidth: '100%', overflow: 'auto' }}>
                      {JSON.stringify(content.backgroundProcess.extraDetails, null, 2)}
                    </Code>
                  </>
                )}
              </Stack>
            ) : (
              <Text c="dimmed">No background process information</Text>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="model" pt="md">
            {content?.dataUploadModel ? (
              <Stack gap="xs">
                <MetadataRow label="id" value={content.dataUploadModel.id} />
                <MetadataRow label="displayName" value={content.dataUploadModel.displayName} />
                <MetadataRow label="description" value={content.dataUploadModel.description} />
                <MetadataRow label="displayOrder" value={content.dataUploadModel.displayOrder} />
                <MetadataRow label="shortName" value={content.dataUploadModel.shortName} />
              </Stack>
            ) : (
              <Text c="dimmed">No data upload model information</Text>
            )}
          </Tabs.Panel>
        </Tabs>
      )}
    </Modal>
  );
}
